<template>
  <div>
    <h2 class="primary--text mb-3">
      Purchase Orders
    </h2>

    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            <span
              v-if="!filters.isArchived"
              class="primary--text"
            >
              Select POs for Processing
              <span
                v-if="selectedPurchaseOrderIds.length"
                class="mx-1 grey--text caption"
              >{{ selectedPurchaseOrderIds.length }} Selected</span>
            </span>
            <span
              v-else
              class="primary--text"
            >List of Archived POs</span>

            <v-spacer />

            <v-checkbox
              v-model="filters.isArchived"
              hide-details
              label="Show Archived POs"
              class="mt-0 pt-0 mx-5"
              @change="fetchPurchaseOrderLines"
            />

            <v-col
              class="py-0 mx-5"
              cols="3"
            >
              <h-date-range-picker
                v-model="filters.dateRange"
                label="Filter by Date"
                dense
                outlined
                hide-details
                @input="fetchPurchaseOrderLines"
              />
            </v-col>

            <v-btn
              v-if="!filters.isArchived"
              :disabled="!selectedPurchaseOrderIds.length"
              depressed
              color="primary"
              @click="prepareDeliveries"
            >
              Prepare Deliveries
            </v-btn>
          </v-card-title>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            <span class="primary--text">Purchase Orders</span>

            <v-spacer />

            <v-btn
              v-if="purchaseOrders.find(isInvalidPo) && !filters.isArchived"
              outlined
              color="red"
              class="mx-2"
              @click="displayArchiveInvalidConfirmation"
            >
              Archive All Invalid
            </v-btn>

            <v-btn
              v-if="purchaseOrders.find(isInvalidPo) && filters.isArchived"
              outlined
              color="red"
              class="mx-2"
              @click="displayDeleteInvalidConfirmation"
            >
              Delete All Invalid
            </v-btn>

            <v-btn
              v-if="selectedPurchaseOrderIds.length && !filters.isArchived"
              outlined
              color="red"
              class="mx-2"
              @click="displayArchiveSelectedConfirmation"
            >
              Archive Selected
            </v-btn>

            <v-btn
              v-if="selectedPurchaseOrderIds.length && filters.isArchived"
              outlined
              color="red"
              class="mx-2"
              @click="displayDeleteSelectedConfirmation"
            >
              Delete Selected
            </v-btn>

            <v-btn
              v-if="selectedPurchaseOrderIds.length && filters.isArchived"
              outlined
              color="primary"
              class="mx-2"
              @click="displayUnArchiveSelectedConfirmation"
            >
              Move Selected To Backlog
            </v-btn>

            <v-btn
              v-if="!filters.isArchived"
              :loading="isUploading"
              outlined
              color="primary"
              class="mx-2"
              @click="chooseFiles"
            >
              Upload PO CSV
            </v-btn>

            <input
              id="fileUpload"
              type="file"
              accept=".csv"
              hidden
              @change="uploadPurchaseOrderCsv"
            >
          </v-card-title>

          <v-divider />

          <v-card-text class="pa-0">
            <v-data-table
              :value="selectedPurchaseOrders"
              :headers="headers"
              :loading="isLoading"
              :items="purchaseOrders"
              :footer-props="{ itemsPerPageOptions :[10, 20, 30] }"
              show-select
              single-expand
              item-key="id"
              @toggle-select-all="onSelectAllPo"
            >
              <template v-slot:item="props">
                <tr>
                  <td
                    class="pr-0"
                    width="1%"
                  >
                    <v-checkbox
                      :disabled="isInvalidPo(props.item)"
                      :input-value="!!selectedPurchaseOrdersHash[props.item.id]"
                      hide-details
                      class="pa-0 ma-0"
                      @change="onSelectSinglePo($event, props.item)"
                    />
                  </td>

                  <td
                    class="pl-0 cursor-pointer"
                    @click="toggleExpand(props)"
                  >
                    <div
                      class="d-flex align-center"
                    >
                      <v-icon
                        color="primary"
                        class="mx-1"
                        v-text="props.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                      />
                      {{ props.item.po_number }}

                      <v-tooltip
                        v-if="isInvalidPo(props.item)"
                        top
                      >
                        <template v-slot:activator="{ on }">
                          <v-icon
                            class="orange--text subtitle-1 font-weight-black mx-2"
                            v-on="on"
                          >
                            mdi-alert
                          </v-icon>
                        </template>

                        <span> {{ props.item.invalidReasons.join('. ') }} </span>
                      </v-tooltip>
                    </div>
                  </td>

                  <td
                    class="cursor-pointer"
                    @click="toggleExpand(props)"
                  >
                    {{ props.item.retailer && props.item.retailer.customer_po_id || 'N/A' }}
                  </td>
                  <td
                    class="cursor-pointer"
                    @click="toggleExpand(props)"
                  >
                    {{ props.item.retailer && props.item.retailer.name || 'N/A' }}
                  </td>
                  <td
                    class="cursor-pointer py-2"
                    @click="toggleExpand(props)"
                  >
                    {{ props.item.store && props.item.store.formatted_address || 'N/A' }}
                  </td>
                  <td
                    class="cursor-pointer text-no-wrap"
                    @click="toggleExpand(props)"
                  >
                    {{ humanizeDateFromSeconds(props.item.poDate, 'YYYY-MM-DD') || 'N/A' }}
                  </td>
                  <td
                    class="cursor-pointer text-no-wrap"
                    @click="toggleExpand(props)"
                  >
                    {{ humanizeDateFromSeconds(props.item.deliveryDate, 'YYYY-MM-DD') || 'N/A' }}
                  </td>
                  <td
                    class="cursor-pointer"
                    @click="toggleExpand(props)"
                  >
                    <v-chip
                      v-if="props.item.status"
                      :color="getStatusColor(props.item.status)"
                      small
                      dark
                      label
                    >
                      {{ props.item.status }}
                    </v-chip>
                    <span v-else>N/A</span>
                  </td>
                  <td class="text-no-wrap">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon
                          class="mx-1"
                          color="grey"
                          v-on="on"
                          @click="displayDetailDialog(props.item)"
                          v-text="'mdi-eye-outline'"
                        />
                      </template>
                      <span>View Details</span>
                    </v-tooltip>

                    <v-tooltip
                      v-if="!filters.isArchived"
                      top
                    >
                      <template v-slot:activator="{ on }">
                        <v-icon
                          class="mx-1"
                          color="grey"
                          v-on="on"
                          @click="displayArchiveConfirmation(props.item)"
                          v-text="'mdi-delete-outline'"
                        />
                      </template>
                      <span>Archive</span>
                    </v-tooltip>

                    <template v-else>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon
                            class="mx-1"
                            color="grey"
                            v-on="on"
                            @click="displayDeleteConfirmation(props.item)"
                            v-text="'mdi-delete-outline'"
                          />
                        </template>
                        <span>Delete</span>
                      </v-tooltip>

                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon
                            class="mx-1"
                            color="grey"
                            v-on="on"
                            @click="displayUnArchiveConfirmation(props.item)"
                            v-text="'mdi-refresh'"
                          />
                        </template>
                        <span>Un Archive</span>
                      </v-tooltip>
                    </template>
                  </td>
                </tr>
              </template>

              <template v-slot:expanded-item="props">
                <tr
                  class="grey lighten-4"
                >
                  <td
                    class="pa-5"
                    :colspan="headers.length + 1"
                  >
                    <div class="primary--text subtitle-1 mb-2">
                      Purchase Order Lines
                    </div>

                    <v-card outlined>
                      <v-data-table
                        hide-default-footer
                        :headers="poLineHeaders"
                        :items="props.item.poLines"
                      >
                        <template v-slot:item="productProp">
                          <tr>
                            <td>{{ productProp.index + 1 }}</td>
                            <td>
                              <span class="mt-0">
                                {{ productProp.item.product &&
                                  productProp.item.product.description ||
                                  'N/A'
                                }}
                              </span>

                              <v-tooltip
                                v-if="productProp.item.isInvalid"
                                top
                              >
                                <template v-slot:activator="{ on }">
                                  <v-icon
                                    class="orange--text subtitle-1 font-weight-black mx-2"
                                    v-on="on"
                                  >
                                    mdi-alert
                                  </v-icon>
                                </template>

                                <span> Invalid </span>
                              </v-tooltip>
                            </td>
                            <td>
                              {{
                                productProp.item.product &&
                                  productProp.item.product.vendorReferenceCode ||
                                  'N/A'
                              }}
                            </td>
                            <td>{{ productProp.item.ean || 'N/A' }}</td>
                            <td>{{ productProp.item.productQuantity || 'N/A' }}</td>
                          </tr>
                        </template>
                      </v-data-table>
                    </v-card>

                    <div class="primary--text subtitle-1 mb-2 mt-5">
                      Pallets
                    </div>
                    <v-card
                      outlined
                    >
                      <v-card-text
                        v-if="selectedPallets.isLoading === true"
                        class="text-center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="primary"
                        />
                      </v-card-text>

                      <v-data-table
                        v-else
                        single-expand
                        item-key="id"
                        hide-default-footer
                        :headers="palletHeaders"
                        :items="selectedPallets.list"
                      >
                        <template v-slot:item="palletProp">
                          <tr
                            class="cursor-pointer"
                            @click="palletProp.expand(!palletProp.isExpanded)"
                          >
                            <td>
                              <v-icon
                                color="primary"
                                class="mx-1"
                                v-text="
                                  palletProp.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                              />

                              {{ palletProp.item.aggregateWeight.value }}
                              {{ palletProp.item.aggregateWeight.units }}
                            </td>
                            <td>
                              £ {{ palletProp.item.palletPrice }}
                            </td>
                            <td>
                              {{ getTotalProducts(palletProp.item) }}
                            </td>
                          </tr>
                        </template>

                        <template v-slot:expanded-item="palletProp">
                          <tr class="grey lighten-4">
                            <td
                              class="pa-5"
                              :colspan="palletHeaders.length + 1"
                            >
                              <div class="primary--text subtitle-1 mb-2">
                                Products
                              </div>

                              <v-card outlined>
                                <v-data-table
                                  hide-default-footer
                                  :headers="productHeaders"
                                  :items="palletProp.item.products"
                                />
                              </v-card>
                            </td>
                          </tr>
                        </template>
                      </v-data-table>
                    </v-card>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-if="detailDialog.isOpen"
      v-model="detailDialog.isOpen"
      fullscreen
      scrollable
      max-width="1000"
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-card-title class="primary white--text py-2 elevation-5">
          <span class="font-weight-regular">{{ detailDialog.data.formatted_address }}</span>
          <v-spacer />
          <v-btn
            text
            large
            dark
            @click="resetDetailDialog"
          >
            Close
          </v-btn>
        </v-card-title>

        <v-card-text class="black--text pa-5 grey lighten-3">
          <po-details
            :details="detailDialog.data"
            @cancel="resetDetailDialog"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <confirmation-dialog
      v-if="confirmationDialog.isOpen"
      :color="confirmationDialog.color"
      :subtitle="confirmationDialog.subtitle"
      :is-accept-button-loading="confirmationDialog.isAcceptButtonLoading"
      @close="resetConfirmationDialog"
      @accept="confirmationDialog.callback"
    />

    <v-dialog
      v-if="progressDialog.isOpen"
      v-model="progressDialog.isOpen"
      max-width="1000"
      persistent
    >
      <v-card>
        <v-card-title class="primary white--text elevation-5 py-3 subtitle-1">
          Deliveries
          <v-spacer />

          <v-icon
            v-if="progressDialog.solverStatus.percentage === 100"
            small
            dark
            @click="resetProgressDialog"
          >
            mdi-close
          </v-icon>
        </v-card-title>

        <v-card-text class="black--text pa-10 grey lighten-3">
          <template v-if="progressDialog.solverStatus.percentage === 100">
            <div class="text-center">
              <template v-if="progressDialog.solverStatus.error">
                <v-icon
                  size="50"
                  color="red"
                  class="mb-5"
                >
                  mdi-alert-circle-outline
                </v-icon>

                <div
                  class="text-capitalize red--text body-1 font-weight-bold"
                >
                  {{ progressDialog.solverStatus.status }}
                </div>
              </template>

              <template v-else>
                <v-icon
                  size="50"
                  color="green"
                  class="mb-2"
                >
                  mdi-check-circle-outline
                </v-icon>

                <div
                  class="green--text headline text-uppercase font-weight-bold"
                >
                  Deliveries have been submitted
                </div>

                <div class="body-1 my-5 grey--text text--darken-3 px-md-12">
                  Your new Purchase Order Batch has now been created and booked
                  for the delivery as per requested schedule. To view the order
                  status, please visit Order Management.
                </div>

                <div>
                  <v-btn
                    :loading="isDownloadingRoutesCsv"
                    depressed
                    color="primary"
                    class="text-capitalize"
                    @click="downloadRoutesCsv"
                  >
                    Download Routes CSV
                  </v-btn>

                  <v-btn
                    :loading="isDownloadingBNQCsv"
                    depressed
                    color="primary"
                    class="ma-3 text-capitalize"
                    @click="downloadBNQCsv"
                  >
                    Download B&Q Bookings CSV
                  </v-btn>

                  <v-btn
                    depressed
                    color="primary"
                    class="text-capitalize"
                    @click="$router.replace('/delivery-requests')"
                  >
                    Go to Delivery Order Management
                  </v-btn>
                </div>
              </template>
            </div>
          </template>

          <template v-else>
            <div
              class="green--text headline text-uppercase font-weight-bold text-center"
            >
              Preparing Your Deliveries
            </div>

            <div class="mt-6 primary--text">
              <strong>{{ progressDialog.solverStatus.percentage || 0 }}%</strong>
              &nbsp;
              <span
                class="text-capitalize"
              >{{ progressDialog.solverStatus.status || 'Preparing' }}</span>

              <v-progress-linear
                class="mt-3"
                :height="10"
                :value="progressDialog.solverStatus.percentage"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.isOpen"
      :color="snackbar.color"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import moment from 'moment';
import HDateRangePicker from '@/components/commons/HDateRangePicker.vue';
import PoDetails from '@/components/purchase-order-manager/PoDetails.vue';
import ConfirmationDialog from '@/components/commons/ConfirmationDialog.vue';

import {
  purchaseOrdersUpload,
  deletePurchaseOrders,
  archivePurchaseOrders,
  createDeliveryRequest,
  unArchivePurchaseOrders,
} from '@/endpoints';
import {
  secureAuthSupplier,
  solverStatusCollection,
  purchaseOrdersCollection,
  purchaseOrderLinesCollection,
  purchaseOrderPalletsCollection,
} from '@/firebase';
import { humanizeDateFromSeconds, downloadCsv } from '@/utils/misc';
import { parsePurchaseOrderLines, parsePallets } from '@/utils/documentParser';

function defaultConfirmationDialog() {
  return {
    color: '',
    subtitle: '',
    isOpen: false,
    callback: () => {},
    isAcceptButtonLoading: false,
  };
}

function groupByPurchaseOrder(poLines) {
  const result = poLines.reduce((purchaseOrders, poLine) => {
    if (purchaseOrders[poLine.purchaseOrderRef.id]) {
      purchaseOrders[poLine.purchaseOrderRef.id].poLines.push(poLine);
      return purchaseOrders;
    }

    /* eslint-disable-next-line no-param-reassign */
    purchaseOrders[poLine.purchaseOrderRef.id] = { ...poLine.purchaseOrder };
    /* eslint-disable-next-line no-param-reassign */
    purchaseOrders[poLine.purchaseOrderRef.id].poLines = [poLine];
    return purchaseOrders;
  }, {});

  return Object.values(result);
}

function defaultSelectedPallets() {
  return {
    isLoading: false,
    list: [],
  };
}

export default {
  components: { HDateRangePicker, PoDetails, ConfirmationDialog },
  data() {
    return {
      headers: [
        { text: 'Vender PO ID', align: 'start', value: 'po_number' },
        { text: 'Customer (Retailer) PO ID', value: 'retailer.po_id', sortable: false },
        { text: 'Customer (Retailer)', value: 'retailer.name', sortable: false },
        { text: 'Delivery Location', value: 'location', sortable: false },
        { text: 'PO Recieved', value: 'po_recieved', sortable: false },
        { text: 'PO Deadline', value: 'po_deadline', sortable: false },
        {
          text: 'PO Status', alue: 'status', align: 'center', sortable: false,
        },
        { text: 'Actions', align: 'center', sortable: false },
      ],
      poLineHeaders: [
        { text: 'PO Line', sortable: false },
        {
          text: 'PO Line Description',
          align: 'start',
          value: 'name',
          sortable: false,
        },
        { text: 'Vendor SKU Code', value: 'vrc', sortable: false },
        { text: 'EAN', value: 'ean', sortable: false },
        { text: 'Order Quantity', value: 'ean', sortable: false },
      ],
      palletHeaders: [
        { text: 'Agg. Pallet Weight', value: 'aggregateWeight' },
        { text: 'Comparative Pallet Price', value: 'palletPrice' },
        {
          text: 'Units on this pallet', sortable: false,
        },
      ],
      productHeaders: [
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Is Topper', value: 'isTopper', sortable: false },
        { text: 'Is Toppable', value: 'isTopable', sortable: false },
        { text: 'Quantity on this pallet', value: 'quantity', sortable: false },
      ],
      selectedPurchaseOrdersHash: {},
      purchaseOrders: [],
      detailDialog: {
        data: {},
        isOpen: false,
      },
      progressDialog: {
        isOpen: false,
        solverStatusId: '',
        solverStatus: { percentage: 0 },
      },
      selectedPallets: defaultSelectedPallets(),
      confirmationDialog: defaultConfirmationDialog(),
      isLoading: false,
      isUploading: false,
      isDownloadingBNQCsv: false,
      isDownloadingRoutesCsv: false,
      snackbar: {
        text: '',
        isOpen: false,
        color: 'green',
      },
      filters: {
        dateRange: {
          startDate: moment().startOf('month').format('YYYY-MM-DD'),
          endDate: moment().endOf('month').format('YYYY-MM-DD'),
        },
        isArchived: false,
      },
      statusColorMapper: {
        New: 'red',
        Planned: 'warning',
        'In Progress': 'blue',
        Delivered: 'green',
      },
      unSubscribeSolverStatus: () => {},
      unSubscribePurchaseOrderLines: () => {},
    };
  },
  computed: {
    selectedPurchaseOrders() {
      return Object.values(this.selectedPurchaseOrdersHash);
    },
    selectedPurchaseOrderIds() {
      return Object.keys(this.selectedPurchaseOrdersHash);
    },
  },
  mounted() {
    this.fetchPurchaseOrderLines();
  },
  beforeDestroy() {
    this.unSubscribeSolverStatus();
    this.unSubscribePurchaseOrderLines();
  },
  methods: {
    humanizeDateFromSeconds,
    onSelectSinglePo(isSelected, value) {
      if (isSelected) {
        this.selectedPurchaseOrdersHash = {
          [value.id]: value,
          ...this.selectedPurchaseOrdersHash,
        };
      } else {
        delete this.selectedPurchaseOrdersHash[value.id];
      }

      this.selectedPurchaseOrdersHash = { ...this.selectedPurchaseOrdersHash };
    },
    onSelectAllPo(event) {
      const isSelected = event.value;

      if (isSelected) {
        this.selectAllPo();
      } else {
        this.selectedPurchaseOrdersHash = {};
      }
    },
    selectAllPo() {
      this.selectedPurchaseOrdersHash = [...this.purchaseOrders].reduce((acc, purchaseOrder) => {
        if (this.isInvalidPo(purchaseOrder)) {
          return acc;
        }

        return {
          ...acc,
          [purchaseOrder.id]: purchaseOrder,
        };
      }, {});
    },
    isInvalidPo(purchaseOrder) {
      return purchaseOrder.isInvalid || purchaseOrder.deliveryDate < purchaseOrder.poDate;
    },
    uploadPurchaseOrderCsv(e) {
      const file = e.target.files[0];
      this.isUploading = true;
      document.getElementById('fileUpload').value = '';

      const formData = new FormData();
      formData.append('purchase_order_csv', file);

      this.axios
        .post(purchaseOrdersUpload, formData)
        .then(() => {
          this.displaySnackbar({
            color: 'green',
            text: 'Purchase Orders were uploaded',
          });

          this.fetchPurchaseOrderLines();
          this.isUploading = false;
        })
        .catch(() => {
          this.isUploading = false;

          this.displaySnackbar({
            color: 'red',
            text: 'Purchase Orders could not be uploaded',
          });
        });
    },
    chooseFiles() {
      document.getElementById('fileUpload').click();
    },
    fetchPurchaseOrderLines() {
      this.isLoading = true;
      this.unSubscribePurchaseOrderLines();

      const query = this.generateFilters(purchaseOrderLinesCollection);

      this.unSubscribePurchaseOrderLines = query
        .onSnapshot((querySnapshot) => {
          this.isLoading = false;

          const poLines = querySnapshot.docs.map(parsePurchaseOrderLines);

          this.purchaseOrders = groupByPurchaseOrder(poLines);

          this.selectAllPo();
        },
        () => {
          this.isLoading = false;

          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
        });
    },
    generateFilters(collection) {
      let query = secureAuthSupplier(collection);

      query = query.where('po_data.is_archived', '==', this.filters.isArchived);

      query = query.where('delivery_request', '==', null);

      query = query.orderBy('created_at', 'desc');

      if (this.filters.dateRange.startDate || this.filters.dateRange.endDate) {
        const start = new Date(this.filters.dateRange.startDate);
        const end = new Date(this.filters.dateRange.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        query = query
          .where('created_at', '>=', start)
          .where('created_at', '<=', end);
      }

      return query;
    },
    toggleExpand(props) {
      if (props.isExpanded) {
        props.expand(!props.isExpanded);
        this.selectedPallets = { ...defaultSelectedPallets() };
        return;
      }

      props.expand(!props.isExpanded);
      this.fetchPallets(props.item);
    },
    fetchPallets(purchaseOrder) {
      this.selectedPallets.isLoading = true;
      const query = secureAuthSupplier(purchaseOrderPalletsCollection);

      query
        .where('po_ref', '==', purchaseOrdersCollection.doc(purchaseOrder.id))
        .get()
        .then((response) => {
          this.selectedPallets.isLoading = false;

          this.selectedPallets.list = response.docs.map(parsePallets);
        })
        .catch(() => {
          this.selectedPallets = defaultSelectedPallets();

          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
        });
    },
    prepareDeliveries() {
      this.displayProgressDialog();

      const data = {
        po_ids: this.selectedPurchaseOrderIds,
      };
      this.axios
        .post(createDeliveryRequest, data)
        .then((response) => {
          this.progressDialog.solverStatusId = response.data.solver_status_id;

          this.fetchDeliveryRequest();
        })
        .catch(() => {
          this.resetProgressDialog();

          this.displaySnackbar({
            color: 'red',
            text: 'The deliveries could not be prepared',
          });
        });
    },
    fetchDeliveryRequest() {
      this.unSubscribeSolverStatus();

      this.unSubscribeSolverStatus = solverStatusCollection
        .doc(this.progressDialog.solverStatusId)
        .onSnapshot((snapshot) => {
          const solverResult = snapshot.data() || {};

          if (solverResult.percentage === 100) {
            this.fetchPurchaseOrderLines();
          }

          this.progressDialog.solverStatus = solverResult;
        });
    },
    downloadRoutesCsv() {
      this.isDownloadingRoutesCsv = true;

      const data = {
        delivery_request_id:
          this.progressDialog.solverStatus.delivery_request_ref.id,
      };

      this.axios
        .post('download_csv/routes/', data)
        .then((response) => {
          this.isDownloadingRoutesCsv = false;

          downloadCsv(response.data, 'routes');
        })
        .catch(() => {
          this.isDownloadingRoutesCsv = false;

          this.displaySnackbar({
            color: 'red',
            text: 'The CSV could not be downloaded.',
          });
        });
    },
    downloadBNQCsv() {
      this.isDownloadingBNQCsv = true;

      const data = {
        delivery_request_id:
          this.progressDialog.solverStatus.delivery_request_ref.id,
      };

      this.axios
        .post('download_csv/bq/', data)
        .then((response) => {
          this.isDownloadingBNQCsv = false;

          downloadCsv(response.data, 'bnq');
        })
        .catch(() => {
          this.isDownloadingBNQCsv = false;

          this.displaySnackbar({
            color: 'red',
            text: 'The CSV could not be downloaded.',
          });
        });
    },
    displayProgressDialog() {
      this.progressDialog = {
        ...this.progressDialog,
        isOpen: true,
      };
    },
    displayDetailDialog(poDetails) {
      this.detailDialog = {
        isOpen: true,
        data: poDetails.store,
      };
    },
    displayDeleteInvalidConfirmation() {
      const subtitle = 'Are you sure you want to delete all invalid purchase orders?';

      const invalidPoIds = this.purchaseOrders.filter(this.isInvalidPo).map((po) => po.id);

      this.confirmationDialog = {
        ...this.confirmationDialog,
        subtitle,
        isOpen: true,
        color: 'error',
        callback: () => this.deleteSelectedPurchaseOrders(invalidPoIds),
      };
    },
    displayArchiveInvalidConfirmation() {
      const subtitle = 'Are you sure you want to archive all invalid purchase orders?';

      const invalidPoIds = this.purchaseOrders.filter(this.isInvalidPo).map((po) => po.id);

      this.confirmationDialog = {
        ...this.confirmationDialog,
        subtitle,
        isOpen: true,
        color: 'error',
        callback: () => this.archiveSelectedPurchaseOrders(invalidPoIds),
      };
    },
    displayDeleteSelectedConfirmation() {
      const subtitle = `Are you sure you want to delete ${this.selectedPurchaseOrderIds.length} purchase orders?`;

      this.confirmationDialog = {
        ...this.confirmationDialog,
        subtitle,
        isOpen: true,
        color: 'error',
        callback: () => this.deleteSelectedPurchaseOrders(
          this.selectedPurchaseOrderIds,
        ),
      };
    },
    displayArchiveSelectedConfirmation() {
      const subtitle = `Are you sure you want to archive ${this.selectedPurchaseOrderIds.length} purchase orders?`;

      this.confirmationDialog = {
        ...this.confirmationDialog,
        subtitle,
        isOpen: true,
        color: 'error',
        callback: () => this.archiveSelectedPurchaseOrders(
          this.selectedPurchaseOrderIds,
        ),
      };
    },
    displayUnArchiveSelectedConfirmation() {
      const subtitle = `Are you sure you want to un archive ${this.selectedPurchaseOrderIds.length} purchase orders?`;

      this.confirmationDialog = {
        ...this.confirmationDialog,
        subtitle,
        isOpen: true,
        color: 'primary',
        callback: () => this.unArchiveSelectedPurchaseOrders(
          this.selectedPurchaseOrderIds,
        ),
      };
    },
    deleteSelectedPurchaseOrders(selectedPoIds) {
      const url = deletePurchaseOrders;
      const data = { ids: selectedPoIds };

      this.confirmationDialog.isAcceptButtonLoading = true;

      this.axios
        .post(url, data)
        .then(() => {
          this.confirmationDialog = defaultConfirmationDialog();

          this.fetchPurchaseOrderLines();

          this.displaySnackbar({
            color: 'green',
            text: 'The purchase orders were deleted',
          });
        })
        .catch(() => {
          this.confirmationDialog = defaultConfirmationDialog();

          this.displaySnackbar({
            color: 'red',
            text: 'The purchase orders could not be deleted',
          });
        });
    },
    archiveSelectedPurchaseOrders(selectedPoIds) {
      const url = archivePurchaseOrders;
      const data = { ids: selectedPoIds };

      this.confirmationDialog.isAcceptButtonLoading = true;

      this.axios
        .post(url, data)
        .then(() => {
          this.confirmationDialog = defaultConfirmationDialog();

          this.fetchPurchaseOrderLines();

          this.displaySnackbar({
            color: 'green',
            text: 'The purchase orders were archived',
          });
        })
        .catch(() => {
          this.confirmationDialog = defaultConfirmationDialog();

          this.displaySnackbar({
            color: 'red',
            text: 'The purchase orders could not be archived',
          });
        });
    },
    unArchiveSelectedPurchaseOrders(selectedPoIds) {
      const url = unArchivePurchaseOrders;
      const data = { ids: selectedPoIds };

      this.confirmationDialog.isAcceptButtonLoading = true;

      this.axios
        .post(url, data)
        .then(() => {
          this.confirmationDialog = defaultConfirmationDialog();

          this.fetchPurchaseOrderLines();

          this.displaySnackbar({
            color: 'green',
            text: 'The purchase orders were un archived',
          });
        })
        .catch(() => {
          this.confirmationDialog = defaultConfirmationDialog();

          this.displaySnackbar({
            color: 'red',
            text: 'The purchase orders could not be un archived',
          });
        });
    },
    displayDeleteConfirmation(purchaseOrder) {
      const subtitle = 'Are you sure you want to delete this purchase orders?';

      this.confirmationDialog = {
        ...this.confirmationDialog,
        subtitle,
        isOpen: true,
        color: 'error',
        callback: () => this.deletePurchaseOrder(purchaseOrder.id),
      };
    },
    displayArchiveConfirmation(purchaseOrder) {
      const subtitle = 'Are you sure you want to archive this purchase orders?';

      this.confirmationDialog = {
        ...this.confirmationDialog,
        subtitle,
        isOpen: true,
        color: 'error',
        callback: () => this.archivePurchaseOrder(purchaseOrder.id),
      };
    },
    displayUnArchiveConfirmation(purchaseOrder) {
      const subtitle = 'Are you sure you want to un archive this purchase orders?';

      this.confirmationDialog = {
        ...this.confirmationDialog,
        subtitle,
        isOpen: true,
        color: 'primary',
        callback: () => this.unArchivePurchaseOrder(purchaseOrder.id),
      };
    },
    archivePurchaseOrder(id) {
      this.archiveSelectedPurchaseOrders([id]);
    },
    unArchivePurchaseOrder(id) {
      this.unArchiveSelectedPurchaseOrders([id]);
    },
    deletePurchaseOrder(id) {
      this.deleteSelectedPurchaseOrders([id]);
    },
    getStatusColor(status) {
      if (!this.statusColorMapper[status]) {
        return '';
      }

      return this.statusColorMapper[status];
    },
    getTotalProducts(pallet) {
      return pallet.products.reduce((sum, product) => sum + product.quantity, 0);
    },
    resetDetailDialog() {
      this.detailDialog = {
        isOpen: false,
        data: {},
      };
    },
    resetConfirmationDialog() {
      this.confirmationDialog = defaultConfirmationDialog();
    },
    resetProgressDialog() {
      this.progressDialog = {
        isOpen: false,
        solverStatusId: '',
        solverStatus: { percentage: 0 },
      };
    },
    displaySnackbar({ text, color }) {
      this.snackbar = {
        text,
        color,
        isOpen: true,
      };
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
